import React from 'react';
import { Palette, Image, Buildings, Check, Upload, X, Moon, Sun, Globe, HardDrive, FloppyDisk } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CompanyNameCard = ({ settings, setSettings }) => (
  <div className="card">
    <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Buildings size={22} weight="bold" /> Nom de la societe</h3></div>
    <div className="card-body">
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label">Nom affiche sur la page de connexion</label>
        <input data-testid="company-name-input" type="text" className="form-input" value={settings.company_name} onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))} placeholder="Nom de votre entreprise" />
      </div>
    </div>
  </div>
);

export const LogoCard = ({ logoPreview, setLogoPreview, settings, setSettings, uploading, setUploading, fileInputRef }) => {
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) { toast.error('Type de fichier non autorise. Utilisez PNG, JPG, SVG ou WebP.'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Le fichier est trop volumineux. Maximum 5 Mo.'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/api/upload/logo`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      setLogoPreview(`${API_URL}${response.data.logo_url}`);
      toast.success('Logo uploade avec succes');
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload'); }
    finally { setUploading(false); }
  };

  return (
    <div className="card">
      <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Image size={22} weight="bold" /> Logo de l'entreprise</h3></div>
      <div className="card-body">
        <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp" style={{ display: 'none' }} onChange={handleFileUpload} />
        {logoPreview ? (
          <div style={{ padding: '24px', backgroundColor: '#F4F5F7', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <img src={logoPreview} alt="Logo" style={{ maxHeight: '80px', maxWidth: '200px', objectFit: 'contain' }} onError={() => setLogoPreview('')} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}><Upload size={16} /> Changer</button>
              <button type="button" className="btn btn-danger btn-sm btn-icon" onClick={() => { setSettings(prev => ({ ...prev, logo_url: '' })); setLogoPreview(''); }} title="Supprimer"><X size={16} /></button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '40px', backgroundColor: '#F4F5F7', borderRadius: '4px', border: '2px dashed #E2E2E6', textAlign: 'center', cursor: 'pointer' }} onClick={() => fileInputRef.current?.click()}>
            <Upload size={40} color="#5E5F66" style={{ marginBottom: '12px' }} />
            <p style={{ color: '#5E5F66', marginBottom: '8px' }}>{uploading ? 'Upload en cours...' : 'Cliquez pour uploader votre logo'}</p>
            <p style={{ fontSize: '0.75rem', color: '#9E9E9E' }}>PNG, JPG, SVG ou WebP - Max 5 Mo</p>
          </div>
        )}
      </div>
    </div>
  );
};

const THEME_COLORS = [
  { name: 'BATI-BOYS Rouge', value: '#C62828', textColor: '#FFFFFF' },
  { name: 'Bleu Klein', value: '#002FA7', textColor: '#FFFFFF' },
  { name: 'Vert Emeraude', value: '#00875A', textColor: '#FFFFFF' },
  { name: 'Rouge Bordeaux', value: '#8B0000', textColor: '#FFFFFF' },
  { name: 'Violet', value: '#6B21A8', textColor: '#FFFFFF' },
  { name: 'Orange', value: '#EA580C', textColor: '#FFFFFF' },
  { name: 'Bleu Marine', value: '#1E3A5F', textColor: '#FFFFFF' },
  { name: 'Gris Anthracite', value: '#374151', textColor: '#FFFFFF' },
  { name: 'Rose Fuchsia', value: '#DB2777', textColor: '#FFFFFF' },
];

export const ThemeColorsCard = ({ settings, onColorSelect }) => (
  <div className="card">
    <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Palette size={22} weight="bold" /> Couleur principale</h3></div>
    <div className="card-body">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
        {THEME_COLORS.map((color) => (
          <button key={color.value} data-testid={`color-${color.value}`} onClick={() => onColorSelect(color.value)}
            style={{ padding: '16px', backgroundColor: color.value, color: color.textColor, border: settings.primary_color === color.value ? '3px solid #0A0B10' : '3px solid transparent', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: '600', fontSize: '0.875rem', transition: 'all 0.2s ease' }}>
            {color.name} {settings.primary_color === color.value && <Check size={20} weight="bold" />}
          </button>
        ))}
      </div>
      <div style={{ marginTop: '20px' }}>
        <label className="form-label">Couleur personnalisee</label>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input type="color" value={settings.primary_color} onChange={(e) => onColorSelect(e.target.value)} style={{ width: '50px', height: '40px', padding: '0', border: '1px solid #E2E2E6', borderRadius: '4px', cursor: 'pointer' }} />
          <input data-testid="custom-color-input" type="text" className="form-input" value={settings.primary_color} onChange={(e) => onColorSelect(e.target.value)} placeholder="#002FA7" style={{ maxWidth: '150px' }} />
        </div>
      </div>
    </div>
  </div>
);

export const DisplayModeCard = ({ settings, setSettings }) => (
  <div className="card">
    <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>{settings.dark_mode ? <Moon size={22} weight="bold" /> : <Sun size={22} weight="bold" />} Mode d'affichage</h3></div>
    <div className="card-body">
      <div style={{ display: 'flex', gap: '12px' }}>
        <button data-testid="light-mode-btn" onClick={() => setSettings(prev => ({ ...prev, dark_mode: false }))} style={{ flex: 1, padding: '20px', borderRadius: '4px', cursor: 'pointer', border: !settings.dark_mode ? '2px solid #002FA7' : '2px solid #E2E2E6', backgroundColor: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}><Sun size={32} color="#EF6C00" /><span style={{ fontWeight: '600' }}>Clair</span></button>
        <button data-testid="dark-mode-btn" onClick={() => setSettings(prev => ({ ...prev, dark_mode: true }))} style={{ flex: 1, padding: '20px', borderRadius: '4px', cursor: 'pointer', border: settings.dark_mode ? '2px solid #002FA7' : '2px solid #E2E2E6', backgroundColor: '#1A1A2E', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}><Moon size={32} color="#90CAF9" /><span style={{ fontWeight: '600' }}>Sombre</span></button>
      </div>
    </div>
  </div>
);

export const LanguageCard = ({ settings, setSettings }) => (
  <div className="card">
    <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Globe size={22} weight="bold" /> Langue</h3></div>
    <div className="card-body">
      <div style={{ display: 'flex', gap: '12px' }}>
        {[{ code: 'fr', label: 'Francais', flag: 'FR' }, { code: 'en', label: 'English', flag: 'EN' }].map(lang => (
          <button key={lang.code} data-testid={`lang-${lang.code}-btn`} onClick={() => setSettings(prev => ({ ...prev, language: lang.code }))}
            style={{ flex: 1, padding: '16px', borderRadius: '4px', cursor: 'pointer', border: settings.language === lang.code ? '2px solid #002FA7' : '2px solid #E2E2E6', backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: settings.language === lang.code ? '700' : '400' }}>
            <span style={{ fontSize: '1.2rem', fontWeight: '700' }}>{lang.flag}</span> {lang.label} {settings.language === lang.code && <Check size={16} weight="bold" color="#002FA7" />}
          </button>
        ))}
      </div>
    </div>
  </div>
);

export const PreviewCard = ({ settings, logoPreview }) => (
  <div className="card">
    <div className="card-header"><h3 className="card-title">Apercu</h3></div>
    <div className="card-body">
      <div style={{ padding: '32px', backgroundColor: '#F4F5F7', borderRadius: '4px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
          {logoPreview ? <img src={logoPreview} alt="Logo" style={{ height: '40px', objectFit: 'contain' }} /> : <div style={{ width: '40px', height: '40px', backgroundColor: settings.primary_color, borderRadius: '4px' }} />}
          <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: '700', color: settings.primary_color }}>{settings.company_name || 'BATI-BOYS'}</span>
        </div>
        <button style={{ backgroundColor: settings.primary_color, color: 'white', padding: '12px 24px', border: 'none', borderRadius: '2px', fontWeight: '600', cursor: 'pointer' }}>Bouton exemple</button>
      </div>
    </div>
  </div>
);

export const DriveCard = ({ driveStatus, setDriveStatus }) => (
  <div className="card" data-testid="drive-section">
    <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><HardDrive size={22} weight="bold" /> Stockage Google Drive</h3></div>
    <div className="card-body">
      {driveStatus.connected ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}><div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#2E7D32' }} /><span style={{ fontWeight: '600', color: '#2E7D32' }}>Connecte</span>{driveStatus.email && <span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>({driveStatus.email})</span>}</div>
          <p style={{ fontSize: '0.85rem', color: '#5E5F66', marginBottom: '12px' }}>Les documents salaries sont automatiquement sauvegardes dans le dossier BATI-BOYS de votre Google Drive.</p>
          <button data-testid="disconnect-drive-btn" className="btn btn-secondary" onClick={async () => { try { await axios.get(`${API_URL}/api/drive/disconnect`, { withCredentials: true }); setDriveStatus({ connected: false, email: '' }); toast.success('Google Drive deconnecte'); } catch (e) { toast.error('Erreur deconnexion'); } }}>Deconnecter Google Drive</button>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: '0.85rem', color: '#5E5F66', marginBottom: '12px' }}>Connectez votre Google Drive pour sauvegarder automatiquement les documents dans un dossier BATI-BOYS.</p>
          <button data-testid="connect-drive-btn" className="btn btn-primary" onClick={async () => { try { const res = await axios.get(`${API_URL}/api/drive/connect`, { withCredentials: true }); window.location.href = res.data.authorization_url; } catch (e) { toast.error(e.response?.data?.detail || 'Erreur connexion Google Drive'); } }}><HardDrive size={16} /> Connecter Google Drive</button>
        </div>
      )}
    </div>
  </div>
);
